type AppleDeviceFamily = 'iPhone' | 'iPad';

interface AppleHardwareIdentifierParts {
  family: AppleDeviceFamily;
  modelCode: string;
}

const APPLE_HARDWARE_IDENTIFIER_REGEXP: RegExp = /\b(?:iPhone|iPad)\d+,\d+\b/g;
const APPLE_HARDWARE_IDENTIFIER_PATTERN: RegExp = /\b(?:iPhone|iPad)\d+,\d+\b/;
const APPLE_HARDWARE_IDENTIFIER_EXACT_REGEXP: RegExp = /^(iPhone|iPad)(\d+,\d+)$/;

const APPLE_SIMULATOR_MODEL_NAMES: Readonly<Partial<Record<string, string>>> = {
  i386: 'iPhone Simulator',
  x86_64: 'iPhone Simulator',
  arm64: 'iPhone Simulator',
};

const APPLE_DEVICE_MODEL_SUFFIXES: Readonly<Record<AppleDeviceFamily, Readonly<Partial<Record<string, string>>>>> = {
  iPhone: {
    '1,1': '',
    '1,2': '3G',
    '2,1': '3GS',
    '3,1': '4',
    '3,2': '4',
    '3,3': '4',
    '4,1': '4S',
    '5,1': '5',
    '5,2': '5',
    '5,3': '5C',
    '5,4': '5C',
    '6,1': '5S',
    '6,2': '5S',
    '7,1': '6 Plus',
    '7,2': '6',
    '8,1': '6s',
    '8,2': '6s Plus',
    '8,4': 'SE',
    '9,1': '7',
    '9,2': '7 Plus',
    '9,3': '7',
    '9,4': '7 Plus',
    '10,1': '8',
    '10,2': '8 Plus',
    '10,3': 'X',
    '10,4': '8',
    '10,5': '8 Plus',
    '10,6': 'X',
    '11,2': 'XS',
    '11,4': 'XS Max',
    '11,6': 'XS Max',
    '11,8': 'XR',
    '12,1': '11',
    '12,3': '11 Pro',
    '12,5': '11 Pro Max',
    '12,8': 'SE 2nd Gen',
    '13,1': '12 mini',
    '13,2': '12',
    '13,3': '12 Pro',
    '13,4': '12 Pro Max',
    '14,2': '13 Pro',
    '14,3': '13 Pro Max',
    '14,4': '13 mini',
    '14,5': '13',
    '14,6': 'SE 3rd Gen',
    '14,7': '14',
    '14,8': '14 Plus',
    '15,2': '14 Pro',
    '15,3': '14 Pro Max',
    '15,4': '15',
    '15,5': '15 Plus',
    '16,1': '15 Pro',
    '16,2': '15 Pro Max',
    '17,1': '16 Pro',
    '17,2': '16 Pro Max',
    '17,3': '16',
    '17,4': '16 Plus',
    '17,5': '16e',
    '18,1': '17 Pro',
    '18,2': '17 Pro Max',
    '18,3': '17',
    '18,4': 'Air',
    '18,5': '17e',
  },
  iPad: {
    '1,1': '',
    '1,2': '',
    '2,1': '2nd Gen',
    '2,2': '2nd Gen',
    '2,3': '2nd Gen',
    '2,4': '2nd Gen',
    '3,1': '3rd Gen',
    '3,2': '3rd Gen',
    '3,3': '3rd Gen',
    '2,5': 'mini',
    '2,6': 'mini',
    '2,7': 'mini',
    '3,4': '4th Gen',
    '3,5': '4th Gen',
    '3,6': '4th Gen',
    '4,1': 'Air',
    '4,2': 'Air',
    '4,3': 'Air',
    '4,4': 'mini 2',
    '4,5': 'mini 2',
    '4,6': 'mini 2',
    '4,7': 'mini 3',
    '4,8': 'mini 3',
    '4,9': 'mini 3',
    '5,1': 'mini 4',
    '5,2': 'mini 4',
    '5,3': 'Air 2',
    '5,4': 'Air 2',
    '6,3': 'Pro 9.7-inch',
    '6,4': 'Pro 9.7-inch',
    '6,7': 'Pro 12.9-inch',
    '6,8': 'Pro 12.9-inch',
    '6,11': '5th Gen',
    '6,12': '5th Gen',
    '7,1': 'Pro 12.9-inch 2nd Gen',
    '7,2': 'Pro 12.9-inch 2nd Gen',
    '7,3': 'Pro 10.5-inch',
    '7,4': 'Pro 10.5-inch',
    '7,5': '6th Gen',
    '7,6': '6th Gen',
    '7,11': '7th Gen',
    '7,12': '7th Gen',
    '8,1': 'Pro 11-inch',
    '8,2': 'Pro 11-inch',
    '8,3': 'Pro 11-inch',
    '8,4': 'Pro 11-inch',
    '8,5': 'Pro 12.9-inch 3rd Gen',
    '8,6': 'Pro 12.9-inch 3rd Gen',
    '8,7': 'Pro 12.9-inch 3rd Gen',
    '8,8': 'Pro 12.9-inch 3rd Gen',
    '8,9': 'Pro 11-inch 2nd Gen',
    '8,10': 'Pro 11-inch 2nd Gen',
    '8,11': 'Pro 12.9-inch 4th Gen',
    '8,12': 'Pro 12.9-inch 4th Gen',
    '11,1': 'mini 5th Gen',
    '11,2': 'mini 5th Gen',
    '11,3': 'Air 3rd Gen',
    '11,4': 'Air 3rd Gen',
    '11,6': '8th Gen',
    '11,7': '8th Gen',
    '12,1': '9th Gen',
    '12,2': '9th Gen',
    '14,1': 'mini 6th Gen',
    '14,2': 'mini 6th Gen',
    '13,1': 'Air 4th Gen',
    '13,2': 'Air 4th Gen',
    '13,4': 'Pro 11-inch 3rd Gen',
    '13,5': 'Pro 11-inch 3rd Gen',
    '13,6': 'Pro 11-inch 3rd Gen',
    '13,7': 'Pro 11-inch 3rd Gen',
    '13,8': 'Pro 12.9-inch 5th Gen',
    '13,9': 'Pro 12.9-inch 5th Gen',
    '13,10': 'Pro 12.9-inch 5th Gen',
    '13,11': 'Pro 12.9-inch 5th Gen',
    '13,16': 'Air 5th Gen',
    '13,17': 'Air 5th Gen',
    '13,18': '10th Gen',
    '13,19': '10th Gen',
    '14,3': 'Pro 11-inch 4th Gen',
    '14,4': 'Pro 11-inch 4th Gen',
    '14,5': 'Pro 12.9-inch 6th Gen',
    '14,6': 'Pro 12.9-inch 6th Gen',
    '14,8': 'Air 11-inch 6th Gen',
    '14,9': 'Air 11-inch 6th Gen',
    '14,10': 'Air 13-inch 6th Gen',
    '14,11': 'Air 13-inch 6th Gen',
    '15,3': 'Air 11-inch 7th Gen',
    '15,4': 'Air 11-inch 7th Gen',
    '15,5': 'Air 13-inch 7th Gen',
    '15,6': 'Air 13-inch 7th Gen',
    '15,7': '11th Gen',
    '15,8': '11th Gen',
    '16,1': 'mini 7th Gen',
    '16,2': 'mini 7th Gen',
    '16,3': 'Pro 11-inch M4',
    '16,4': 'Pro 11-inch M4',
    '16,5': 'Pro 13-inch M4',
    '16,6': 'Pro 13-inch M4',
    '16,8': 'Air 11-inch 8th Gen',
    '16,9': 'Air 11-inch 8th Gen',
    '16,10': 'Air 13-inch 8th Gen',
    '16,11': 'Air 13-inch 8th Gen',
    '17,1': 'Pro 11-inch M5',
    '17,2': 'Pro 11-inch M5',
    '17,3': 'Pro 13-inch M5',
    '17,4': 'Pro 13-inch M5',
  },
};

export function formatAppleDeviceModelInText(value: string | null | undefined): string {
  if (!value) return '';

  return value.replace(APPLE_HARDWARE_IDENTIFIER_REGEXP, (identifier: string): string => {
    return getAppleDeviceModelName(identifier) ?? identifier;
  });
}

export function formatDeviceModelName(manufacturer: string | null | undefined, model: string | null | undefined): string {
  const normalizedManufacturer: string = manufacturer?.trim() ?? '';
  const normalizedModel: string = model?.trim() ?? '';

  if (!normalizedModel) {
    return normalizedManufacturer ? `${normalizedManufacturer} Unknown` : 'Unknown';
  }

  const exactAppleModelName: string | null = getAppleDeviceModelName(normalizedModel);

  if (exactAppleModelName) {
    return isAppleManufacturer(normalizedManufacturer)
      ? `${normalizedManufacturer} ${exactAppleModelName}`
      : exactAppleModelName;
  }

  if (APPLE_HARDWARE_IDENTIFIER_PATTERN.test(normalizedModel)) {
    const formattedModel: string = formatAppleDeviceModelInText(normalizedModel);
    return isAppleManufacturer(normalizedManufacturer)
      ? `${normalizedManufacturer} ${formattedModel}`
      : formattedModel;
  }

  if (isAppleManufacturer(normalizedManufacturer)) {
    return `${normalizedManufacturer} ${normalizedModel}`;
  }

  const shortModel: string = normalizedModel.split(' ').slice(0, 2).join(' ');
  return normalizedManufacturer ? `${normalizedManufacturer} ${shortModel}` : shortModel;
}

function getAppleDeviceModelName(identifier: string): string | null {
  const simulatorModelName: string | undefined = APPLE_SIMULATOR_MODEL_NAMES[identifier];

  if (simulatorModelName) {
    return simulatorModelName;
  }

  const parts: AppleHardwareIdentifierParts | null = parseAppleHardwareIdentifier(identifier);

  if (!parts) {
    return null;
  }

  const suffix: string | undefined = APPLE_DEVICE_MODEL_SUFFIXES[parts.family][parts.modelCode];

  if (suffix === undefined) {
    return null;
  }

  return suffix ? `${parts.family} ${suffix}` : parts.family;
}

function parseAppleHardwareIdentifier(identifier: string): AppleHardwareIdentifierParts | null {
  const match: RegExpMatchArray | null = identifier.match(APPLE_HARDWARE_IDENTIFIER_EXACT_REGEXP);
  const family: string | undefined = match?.[1];
  const modelCode: string | undefined = match?.[2];

  if (!family || !modelCode || !isAppleDeviceFamily(family)) {
    return null;
  }

  return {family, modelCode};
}

function isAppleDeviceFamily(value: string): value is AppleDeviceFamily {
  return value === 'iPhone' || value === 'iPad';
}

function isAppleManufacturer(manufacturer: string): boolean {
  return manufacturer.toLowerCase() === 'apple';
}
